import { motion } from 'framer-motion'
import { Trans, useLingui } from '@lingui/react/macro'

import { H2 } from '../../_components/H2'

import {
  getLocalizedIndexImage,
  howToStep01Images,
  howToStep02Images,
  howToStep03Images,
} from './localizedImages'

import LazyImage from '@/components/LazyImage'
import { useLocale } from '@/hooks/routes'
import { useScreen } from '@/hooks/screen'
import { PrimaryButton } from '@/pages/tad.ai/_components/PrimaryButton'


import { useRef, useState, useEffect } from 'react';
import { cls } from 'tagged-classnames-free';
export const HowTo: React.FC = () => {
  const intervalRef = useRef<NodeJS.Timeout>()
  const isMd = useScreen('md')

  const { t } = useLingui()
  const locale = useLocale()

  const list = [
    {
      text: t`Input the title of your song and the lyrics you'd like to use. You have the option to write the lyrics yourself or let our AI music generator create them for you.`,
      img: getLocalizedIndexImage(
        locale,
        '/images/index/step-01.png?t=1',
        howToStep01Images,
      ),
    },
    {
      text: t`Select the genre and mood that best aligns with your creative vision. This step is optional but can help refine the final composition.`,
      img: getLocalizedIndexImage(
        locale,
        '/images/index/step-02.png?t=1',
        howToStep02Images,
      ),
    },
    {
      text: t`Finally, hit the "Create" button, and you'll get a unique piece of music tailored to your specifications in mere seconds.`,
      img: getLocalizedIndexImage(
        locale,
        '/images/index/step-03.png?t=1',
        howToStep03Images,
      ),
    },
  ]
  const [currentIndex, setCurrentIndex] = useState(0)
  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % list.length)
    }, 2000)

    return () => clearInterval(intervalRef.current as NodeJS.Timeout)
  }, [])
  return (
    <div className='mx-auto flex max-w-[1064px] flex-col items-center px-4'>
      <LazyImage
        width={322}
        height={80}
        src={'/images/index/how-to.png'}
        alt={t`How to Generate AI Music for Free With Tad.ai?`}
        className='mx-auto h-8 w-[130px] opacity-30 md:h-20 md:w-[322px]'
      />
      <H2 className='mx-auto pt-1 md:max-w-[880px] md:pt-9'>
        <Trans>How to Generate AI Music for Free With Tad.ai?</Trans>
      </H2>
      {isMd ? (
        <div className='flex items-center gap-12 pt-12'>
          <LazyImage
            width={480}
            height={400}
            src={list[currentIndex].img}
            alt={t`How to Generate AI Music for Free With Tad.ai?`}
            className='rounded-xl'
          />
          <div className='flex flex-1 gap-6'>
            <motion.div
              className='bg-f-primary z-[1] w-px'
              style={{ transformOrigin: 'top' }}
              initial={{ height: 0 }}
              animate={{ height: 285 }}
              transition={{
                duration: 2 * list.length,
                repeat: Infinity,
                repeatType: 'loop',
              }}
            />
            <div className='flex max-w-[480px] flex-col gap-12'>
              {list.map((item, index) => {
                return (
                  <div key={item.text} className='flex items-start gap-6'>
                    <span
                      className={cls`${currentIndex === index ? 'text-f-primary' : 'text-f-other-10'} text-2xl`}
                    >
                      0{index + 1}
                    </span>
                    <p className='text-f-other-10'>{item.text}</p>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      ) : (
        <div className='flex flex-col items-center gap-6 pt-4'>
          {list.map((item, index) => {
            return (
              <div
                key={item.text}
                className='border-f-border flex flex-col gap-4 border-b pb-6'
              >
                <LazyImage
                  width={344}
                  height={288}
                  src={item.img}
                  alt={item.text}
                  className='mx-auto w-full'
                />
                <div className='flex items-start gap-6'>
                  <span className='text-f-primary text-2xl'>0{index + 1}</span>
                  <p className='text-f-other-10 text-sm'>{item.text}</p>
                </div>
              </div>
            )
          })}
        </div>
      )}
      <PrimaryButton href='/ai-music-generator' className='mt-6 md:mt-9'>
        <Trans>Try for Free</Trans>
      </PrimaryButton>
    </div>
  )
}
